import { GIFEncoder, quantize, applyPalette } from "gifenc";
import { Muxer as Mp4Muxer, ArrayBufferTarget as Mp4Target } from "mp4-muxer";
import {
  Muxer as WebmMuxer,
  ArrayBufferTarget as WebmTarget,
} from "webm-muxer";

export type ExportFormat = "gif" | "apng" | "mp4" | "webm";

export type ExportQuality = "low" | "medium" | "high";

export type EncoderOptions = {
  /** GIF/APNG only — video files always play once (players handle looping) */
  loopForever?: boolean;
  /** video bitrate tier; ignored by GIF/APNG */
  quality?: ExportQuality;
};

// bits/pixel/frame — "medium" is the previous fixed 0.1 rate
const QUALITY_BPPF: Record<ExportQuality, number> = {
  low: 0.05,
  medium: 0.1,
  high: 0.2,
};

/** Yield a macrotask without setTimeout — hidden tabs throttle timers to
 * ≥1s (down to 1/min), which made background-tab exports crawl; MessageChannel
 * tasks are exempt. */
export const yieldToEventLoop = () =>
  new Promise<void>((resolve) => {
    const { port1, port2 } = new MessageChannel();
    port1.onmessage = () => {
      port1.close();
      resolve();
    };
    port2.postMessage(null);
  });

export const EXPORT_MIME: Record<ExportFormat, string> = {
  gif: "image/gif",
  // APNG files are valid PNGs; image/png keeps viewers/finder happy
  apng: "image/png",
  mp4: "video/mp4",
  webm: "video/webm",
};

export interface AnimationEncoder {
  addFrame(canvas: HTMLCanvasElement, frameDurationMs: number): Promise<void>;
  finish(): Promise<Blob>;
}

class GifAnimationEncoder implements AnimationEncoder {
  private gif = GIFEncoder();
  // GIF stores delays in 10ms steps; carry the rounding error forward so the
  // total duration stays accurate (naive rounding makes 24fps play ~4% fast,
  // 30fps ~11% fast)
  private idealMs = 0;
  private emittedMs = 0;

  constructor(private loopForever: boolean) {}

  async addFrame(canvas: HTMLCanvasElement, frameDurationMs: number) {
    const ctx = canvas.getContext("2d")!;
    const { data, width, height } = ctx.getImageData(
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const palette = quantize(data, 256);
    const index = applyPalette(data, palette);
    this.idealMs += frameDurationMs;
    const delay = Math.round((this.idealMs - this.emittedMs) / 10) * 10;
    this.emittedMs += delay;
    // repeat=0 writes the NETSCAPE loop extension (loop forever); -1 omits
    // it, so the GIF plays once. gifenc only applies it on the first frame.
    this.gif.writeFrame(index, width, height, {
      palette,
      delay,
      repeat: this.loopForever ? 0 : -1,
    });
  }

  async finish() {
    this.gif.finish();
    return new Blob([this.gif.bytes() as BlobPart], {
      type: EXPORT_MIME.gif,
    });
  }
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array) => {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
};

const pngChunk = (type: string, data: Uint8Array) => {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length);
  for (let i = 0; i < 4; i++) {
    out[4 + i] = type.charCodeAt(i);
  }
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
};

type PngChunk = { type: string; data: Uint8Array };

const parsePngChunks = (bytes: Uint8Array): PngChunk[] => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const chunks: PngChunk[] = [];
  let i = 8; // skip PNG signature
  while (i + 12 <= bytes.length) {
    const length = view.getUint32(i);
    const type = String.fromCharCode(
      bytes[i + 4],
      bytes[i + 5],
      bytes[i + 6],
      bytes[i + 7],
    );
    chunks.push({ type, data: bytes.subarray(i + 8, i + 8 + length) });
    i += 12 + length;
  }
  return chunks;
};

/**
 * Lossless 24-bit animation: splices the browser's own PNG encoder output
 * (canvas.toDataURL) into APNG acTL/fcTL/fdAT chunks — pixel-perfect, no
 * palette, no extra encoder dependency. All frames share the canvas size,
 * which record.ts guarantees.
 */
class ApngAnimationEncoder implements AnimationEncoder {
  private frames: { chunks: PngChunk[]; delayMs: number }[] = [];

  constructor(private fps: number, private loopForever: boolean) {}

  async addFrame(canvas: HTMLCanvasElement, frameDurationMs: number) {
    const dataUrl = canvas.toDataURL("image/png");
    const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      bytes[i] = bin.charCodeAt(i);
    }
    const chunks = parsePngChunks(bytes);
    if (!chunks.some((c) => c.type === "IHDR")) {
      throw new Error("PNG encoding failed.");
    }
    this.frames.push({ chunks, delayMs: frameDurationMs });
  }

  async finish() {
    const [first, ...rest] = this.frames;
    if (!first) {
      throw new Error("No frames to export.");
    }
    const ihdr = first.chunks.find((c) => c.type === "IHDR")!;
    const ihdrView = new DataView(
      ihdr.data.buffer,
      ihdr.data.byteOffset,
      ihdr.data.byteLength,
    );
    const width = ihdrView.getUint32(0);
    const height = ihdrView.getUint32(4);

    const parts: Uint8Array[] = [
      new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
    ];
    // sequence numbers are shared across fcTL and fdAT chunks, in stream order
    let seq = 0;
    const writeFcTL = (delayMs: number) => {
      const data = new Uint8Array(26);
      const view = new DataView(data.buffer);
      view.setUint32(0, seq++);
      view.setUint32(4, width);
      view.setUint32(8, height);
      // x/y offset stay 0 — every frame is a full-canvas replace
      // (dispose_op/blend_op stay 0 too: none/source)
      // delay is a rational; 1/fps is exact where ms/1000 would round
      const exact = Math.abs(delayMs - 1000 / this.fps) < 0.01;
      view.setUint16(20, exact ? 1 : Math.round(delayMs));
      view.setUint16(22, exact ? this.fps : 1000);
      parts.push(pngChunk("fcTL", data));
    };

    // replay the first frame's chunks, inserting acTL + frame 0's fcTL just
    // before its first IDAT (spec: acTL/fcTL must precede IDAT) so ancillary
    // chunks like sRGB/pHYs are preserved where the browser put them
    let frame0Started = false;
    for (const chunk of first.chunks) {
      if (chunk.type === "IEND") {
        continue;
      }
      if (chunk.type === "IDAT" && !frame0Started) {
        frame0Started = true;
        const actl = new Uint8Array(8);
        const actlView = new DataView(actl.buffer);
        actlView.setUint32(0, this.frames.length);
        // num_plays 0 = loop forever, 1 = play once — matches the GIF export
        actlView.setUint32(4, this.loopForever ? 0 : 1);
        parts.push(pngChunk("acTL", actl));
        writeFcTL(first.delayMs);
      }
      parts.push(pngChunk(chunk.type, chunk.data));
    }
    for (const frame of rest) {
      writeFcTL(frame.delayMs);
      for (const chunk of frame.chunks) {
        if (chunk.type !== "IDAT") {
          continue;
        }
        const data = new Uint8Array(4 + chunk.data.length);
        new DataView(data.buffer).setUint32(0, seq++);
        data.set(chunk.data, 4);
        parts.push(pngChunk("fdAT", data));
      }
    }
    parts.push(pngChunk("IEND", new Uint8Array(0)));
    return new Blob(parts as BlobPart[], { type: EXPORT_MIME.apng });
  }
}

// H.264 profile+level ladder, best first: High → Main → Baseline, each with a
// high level (5.2, covers 4096×2304@60) and a lower fallback — so every
// machine finds a supported config and capable ones get the best profile.
const MP4_CODEC_CANDIDATES = [
  "avc1.640034", // High 5.2
  "avc1.640028", // High 4.0
  "avc1.64001f", // High 3.1
  "avc1.4d0034", // Main 5.2
  "avc1.4d0028", // Main 4.0
  "avc1.4d001f", // Main 3.1
  "avc1.420034", // Baseline 5.2
  "avc1.42001f", // Baseline 3.1
];
const WEBM_CODEC = "vp09.00.10.08";

class VideoAnimationEncoder implements AnimationEncoder {
  private encoder: VideoEncoder;
  private muxer: Mp4Muxer<Mp4Target> | WebmMuxer<WebmTarget>;
  private target: Mp4Target | WebmTarget;
  private format: "mp4" | "webm";
  private timestampUs = 0;
  private frameIndex = 0;
  // holder object because the VideoEncoder error callback is wired before
  // this instance exists; the callback closes over it
  private encodeError: { current: Error | null };

  private constructor(
    format: "mp4" | "webm",
    encoder: VideoEncoder,
    muxer: Mp4Muxer<Mp4Target> | WebmMuxer<WebmTarget>,
    target: Mp4Target | WebmTarget,
    encodeError: { current: Error | null },
  ) {
    this.format = format;
    this.encoder = encoder;
    this.muxer = muxer;
    this.target = target;
    this.encodeError = encodeError;
  }

  static async create(
    format: "mp4" | "webm",
    width: number,
    height: number,
    fps: number,
    quality: ExportQuality = "medium",
  ): Promise<VideoAnimationEncoder> {
    if (typeof VideoEncoder === "undefined") {
      throw new Error(
        "Video export requires a browser with WebCodecs support (recent Chrome, Edge, or Safari). Use GIF export instead.",
      );
    }
    // H.264/VP9 require even dimensions
    const evenWidth = width - (width % 2);
    const evenHeight = height - (height % 2);

    let codec = WEBM_CODEC;
    if (format === "mp4") {
      let supported: string | null = null;
      for (const candidate of MP4_CODEC_CANDIDATES) {
        const res = await VideoEncoder.isConfigSupported({
          codec: candidate,
          width: evenWidth,
          height: evenHeight,
        });
        if (res.supported) {
          supported = candidate;
          break;
        }
      }
      if (!supported) {
        throw new Error(
          "No supported H.264 encoder configuration found for this resolution. Try a smaller scale or WebM.",
        );
      }
      codec = supported;
    }

    const target = format === "mp4" ? new Mp4Target() : new WebmTarget();
    const muxer =
      format === "mp4"
        ? new Mp4Muxer({
            target: target as Mp4Target,
            video: { codec: "avc", width: evenWidth, height: evenHeight },
            fastStart: "in-memory",
          })
        : new WebmMuxer({
            target: target as WebmTarget,
            video: { codec: "V_VP9", width: evenWidth, height: evenHeight },
          });

    // throwing inside the error callback would fire in a UA-internal task and
    // never reach the caller; store it and rethrow from addFrame/finish
    const encodeError = { current: null as Error | null };
    const encoder = new VideoEncoder({
      output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
      error: (e) => {
        encodeError.current = e instanceof Error ? e : new Error(String(e));
      },
    });
    encoder.configure({
      codec,
      width: evenWidth,
      height: evenHeight,
      // bits/pixel/frame scales with resolution so line art stays crisp at
      // any export scale; a flat rate smears 2x-3x exports. High tier gets a
      // higher ceiling so 3x-4x exports aren't silently capped back down.
      bitrate: Math.min(
        quality === "high" ? 40_000_000 : 20_000_000,
        Math.max(
          1_000_000,
          Math.round(evenWidth * evenHeight * fps * QUALITY_BPPF[quality]),
        ),
      ),
      framerate: fps,
      // no latencyMode "quality": Chrome's software VP9 encoder stalls the
      // encodeQueueSize backpressure loop in that mode (verified in-browser)
    });

    return new VideoAnimationEncoder(
      format,
      encoder,
      muxer,
      target,
      encodeError,
    );
  }

  async addFrame(canvas: HTMLCanvasElement, frameDurationMs: number) {
    if (this.encodeError.current) {
      throw this.encodeError.current;
    }
    const durationUs = Math.round(frameDurationMs * 1000);
    const frame = new VideoFrame(canvas, {
      timestamp: this.timestampUs,
      duration: durationUs,
    });
    this.encoder.encode(frame, { keyFrame: this.frameIndex % 120 === 0 });
    frame.close();
    this.timestampUs += durationUs;
    this.frameIndex += 1;
    // apply backpressure so we don't queue hundreds of raw frames in memory
    // (yieldToEventLoop, not setTimeout — timer throttling in hidden tabs
    // would stall this loop for minutes)
    while (this.encoder.encodeQueueSize > 2) {
      if (this.encodeError.current) {
        throw this.encodeError.current;
      }
      await yieldToEventLoop();
    }
  }

  async finish() {
    if (this.encodeError.current) {
      throw this.encodeError.current;
    }
    await this.encoder.flush();
    this.muxer.finalize();
    return new Blob([this.target.buffer!], {
      type: EXPORT_MIME[this.format],
    });
  }
}

export const createEncoder = async (
  format: ExportFormat,
  width: number,
  height: number,
  fps: number,
  { loopForever = true, quality = "medium" }: EncoderOptions = {},
): Promise<AnimationEncoder> => {
  if (format === "gif") {
    return new GifAnimationEncoder(loopForever);
  }
  if (format === "apng") {
    return new ApngAnimationEncoder(fps, loopForever);
  }
  return VideoAnimationEncoder.create(format, width, height, fps, quality);
};
